/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState } from 'react';
import {
  gridFilteredSortedRowIdsSelector,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarProps,
  ToolbarPropsOverrides,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { alpha, FormControlLabel, Menu, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Check, DateRange } from '@mui/icons-material';
import { setDateRangeFilter, setDateRangeOptionId, updateDataGridState } from '../ducks/muiDataGridSlice';
import { DateRange as DateRangeType } from '../types/DateRange';
import { getCurrentMonthDateRange, getLastDaysDateRange, getPriorMonthDateRange } from '../utils/dateUtils';
import { useAppSelector } from '../hooks';
import { FilterMenuItems } from '../constants/FilterMenuItems';
import { I18nKeys } from '../constants/I18nKeys';
import { closeDialog, openDialog } from '../ducks/dialogSlice';
import { Dialogs } from '../constants/Dialogs';
import { PageHeader } from './library/PageHeader';
import { PageHeaderButton } from './library/PageHeaderButton';
import { DateRangeDialog } from './DateRangeDialog';
import { MUIDataGridKey } from '../constants/MUIDataGrid';
import { AppState } from '../types/AppState';
import { titleCaseToKebabCase } from '../utils/stringUtils';

interface Props {
  header: string;
  onHeaderClick: () => void;
  section: string;
  disableFilter: boolean;
  enableDateFilter: boolean;
  anchorEl: HTMLButtonElement | undefined;
  setFilterButtonEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
}

const useStyles = makeStyles({
  button: {
    borderRadius: '8px',
    p: '4px',
    pr: '8px',
    '&:hover': {
      backgroundColor: alpha('#000000', 0.04),
    },
  },
  text: {
    fontSize: '14px',
    textTransform: 'none',
    color: alpha('#000000', 0.87),
  },
  icon: {
    color: alpha('#000000', 0.54),
    margin: '0px',
    marginRight: '8px',
  },
});

type DateRangeOption = {
  key: FilterMenuItems;
  label: I18nKeys;
  filterMessage: string;
  dateRangeFilter?: DateRangeType;
};

const dateRangeOptions: DateRangeOption[] = [
  {
    key: FilterMenuItems.Days15,
    label: I18nKeys.FilterBy15Days,
    filterMessage: I18nKeys.OrderFilterLast15Days,
    dateRangeFilter: getLastDaysDateRange(15),
  },
  {
    key: FilterMenuItems.Days30,
    label: I18nKeys.FilterBy30Days,
    filterMessage: I18nKeys.OrderFilterLast30Days,
    dateRangeFilter: getLastDaysDateRange(30),
  },
  {
    key: FilterMenuItems.Days45,
    label: I18nKeys.FilterBy45Days,
    filterMessage: I18nKeys.OrderFilterLast45Days,
    dateRangeFilter: getLastDaysDateRange(45),
  },
  {
    key: FilterMenuItems.Days90,
    label: I18nKeys.FilterBy90Days,
    filterMessage: I18nKeys.OrderFilterLast90Days,
    dateRangeFilter: getLastDaysDateRange(90),
  },
  {
    key: FilterMenuItems.MonthCurrent,
    label: I18nKeys.FilterByThisMonth,
    filterMessage: I18nKeys.OrderFilterThisMonth,
    dateRangeFilter: getCurrentMonthDateRange(),
  },
  {
    key: FilterMenuItems.MonthLast,
    label: I18nKeys.FilterByLastMonth,
    filterMessage: I18nKeys.OrderFilterLastMonth,
    dateRangeFilter: getPriorMonthDateRange(),
  },
  { key: FilterMenuItems.Custom, label: I18nKeys.FilterByCustomDates, filterMessage: I18nKeys.OrderFilterCustom },
];

export const MUIDataGridToolbar: React.FC<GridToolbarProps & ToolbarPropsOverrides & Props> = ({
  header,
  onHeaderClick,
  section,
  setFilterButtonEl,
  disableFilter,
  enableDateFilter,
  countLabel,
  loading,
  anchorEl,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiRef = useGridApiContext();

  const { currentKey: dataGridKey } = useAppSelector((state: AppState) => state.muiDataGrid);
  const { dateRangeOptionId, dateRangeFilter } = useAppSelector(
    (state) => state.muiDataGrid[dataGridKey] || state.muiDataGrid[MUIDataGridKey.Default],
  );

  const [dateRangeFilterOpen, setDateRangeFilterOpen] = useState(false);
  const count = apiRef.current?.getRowsCount();
  const filteredCount = gridFilteredSortedRowIdsSelector(apiRef)?.length;

  const handleClose = useCallback(
    (id = 0) => {
      const { key, dateRangeFilter: filter } = dateRangeOptions[id];
      if (filter) {
        dispatch(setDateRangeFilter({ dateRangeFilter: filter }));
        dispatch(setDateRangeOptionId(id));
        dispatch(updateDataGridState({ dataGridKey, dateRangeOptionId: id, dateRangeFilter: filter }));
      }
      if (key === FilterMenuItems.Custom) {
        dispatch(openDialog({ dialog: Dialogs.DateRange }));
      }

      setDateRangeFilterOpen(false);
    },
    [dispatch],
  );

  const handleDateRangeDialogCancel = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const handleDateRangeDialogApply = useCallback(
    (newDateRange: DateRangeType) => {
      const customDateRangeOptionId = dateRangeOptions.length - 1;
      dispatch(setDateRangeFilter({ dateRangeFilter: newDateRange }));
      dispatch(closeDialog());
      dispatch(setDateRangeOptionId(customDateRangeOptionId));
      dispatch(
        updateDataGridState({ dataGridKey, dateRangeOptionId: customDateRangeOptionId, dateRangeFilter: newDateRange }),
      );
    },
    [dispatch],
  );

  return (
    <GridToolbarContainer sx={{ display: 'block', p: '0px' }}>
      <PageHeader
        id={`${titleCaseToKebabCase(header || dataGridKey)}-header`}
        header={header}
        onHeaderClick={onHeaderClick}
        section={section}
        count={filteredCount != null ? filteredCount : count}
        countLabel={countLabel}
        loading={loading}
        buttons={[
          ...(enableDateFilter
            ? [
                <>
                  <PageHeaderButton
                    ref={setFilterButtonEl}
                    id="header-date-filter-button"
                    icon={<DateRange />}
                    label={t(I18nKeys.MUIDataGridFilterMessage, {
                      optionLabel: t(dateRangeOptions[dateRangeOptionId].filterMessage),
                      startDate: dateRangeFilter.startDate.format('MM/DD/YYYY'),
                      endDate: dateRangeFilter.endDate.format('MM/DD/YYYY'),
                      context: FilterMenuItems[dateRangeOptions[dateRangeOptionId].key],
                    })}
                    onClick={() => setDateRangeFilterOpen(!dateRangeFilterOpen)}
                  />
                  <Menu
                    id="header-filter-menu"
                    anchorEl={anchorEl}
                    open={dateRangeFilterOpen}
                    onClose={() => handleClose()}
                  >
                    {dateRangeOptions.map((option, id) => (
                      <MenuItem
                        disableGutters
                        key={option.key}
                        sx={{ py: '0px', pl: '7px', pr: '16px' }}
                        onClick={() => handleClose(id)}
                      >
                        <FormControlLabel
                          sx={{ mx: '0px' }}
                          control={
                            <Check
                              sx={{
                                m: '9px',
                                color: (theme) =>
                                  id === dateRangeOptionId ? theme.palette.secondary.main : 'transparent',
                              }}
                            />
                          }
                          label={<Typography>{t(option.label)}</Typography>}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                  <DateRangeDialog
                    onApply={handleDateRangeDialogApply}
                    onCancel={handleDateRangeDialogCancel}
                    dateRange={dateRangeFilter}
                  />
                </>,
              ]
            : []),
          ...(!disableFilter
            ? [
                <GridToolbarFilterButton
                  ref={!enableDateFilter ? setFilterButtonEl : undefined}
                  slotProps={{
                    button: {
                      id: 'header-filter-button',
                      className: classes.button,
                      classes: { text: classes.text, icon: classes.icon },
                    },
                    tooltip: {
                      disableFocusListener: true,
                      disableHoverListener: true,
                      disableTouchListener: true,
                    },
                  }}
                />,
              ]
            : []),
        ]}
      />
    </GridToolbarContainer>
  );
};
